import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";

// Bespoke, world-class landing page for SQL Editor & SQLite Viewer (Android).
// Light premium aesthetic, concept-first: a live SQL console motif in the hero,
// real device screenshots in phone mockups, a review wall with one standout,
// an FAQ, and rigorous on-page SEO (meta + OG + JSON-LD
// SoftwareApplication/AggregateRating/Review/FAQPage). Hosted at /apps/sqlvedit.
// Brand: amber #E8912B + indigo #5A56E6 on warm ivory. Assets in /images/sqlvedit.

const PLAY = "https://play.google.com/store/apps/details?id=com.cosmovex.sqlvedit";
const SHOT = "/images/sqlvedit"; // 01..06.png real store screenshots
const LOGO = "/images/play/sqlvedit.png";

const AMBER = "#E8912B";
const AMBER_D = "#B96A0E";
const INDIGO = "#5A56E6";
const INK = "#1A2233";
const SUB = "#5A6678";
const IVORY = "#FBFAF6";
const CARD = "#FFFFFF";
const LINE = "#ECE7DB";

const SANS = `-apple-system, "SF Pro Display", "Segoe UI", Roboto, Helvetica, Arial, sans-serif`;
const MONO = `"SF Mono", "JetBrains Mono", ui-monospace, Menlo, Consolas, monospace`;

const RATING = "5.0";
const RATING_COUNT = "11";

// Real, verbatim 5★ reviews from Google Play.
const REVIEW_WOW =
  "Is it seriously so hard to build a working SQLite client for Android?! This is the first one I've found that just WORKS flawlessly. Actually opens my db files, actually lets me interact with the fields, and it's PRETTY! with a nice UI. Thank you! 6 stars.";
const REVIEW_LEARN =
  "I don't have a laptop, so this SQL app has been really helpful for me. It's great for learning and practicing SQL anytime, anywhere. The lessons are easy to follow, and the practice features make it simple to improve step by step. I would definitely recommend this app to anyone who wants to learn SQL, especially if you're using just a phone like me. Really useful and convenient!";

const playClick = (e: React.MouseEvent) => {
  e.preventDefault();
  const w = window as any;
  if (w.gtag_report_conversion) w.gtag_report_conversion(PLAY);
  else window.open(PLAY, "_blank", "noopener");
};

const PlayButton: React.FC<{ small?: boolean }> = ({ small }) => (
  <a
    href={PLAY}
    onClick={playClick}
    aria-label="Get SQL Editor & SQLite Viewer on Google Play"
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: "0.6rem",
      padding: small ? "0.72rem 1.35rem" : "1.02rem 1.9rem",
      background: `linear-gradient(180deg, ${AMBER} 0%, ${AMBER_D} 100%)`,
      color: "#fff",
      borderRadius: "0.9rem",
      fontWeight: 700,
      fontSize: small ? "0.98rem" : "1.14rem",
      textDecoration: "none",
      border: "1px solid rgba(0,0,0,0.06)",
      boxShadow: "0 1px 2px rgba(120,72,10,0.14), 0 10px 24px -8px rgba(184,106,14,0.45)",
      fontFamily: SANS,
      letterSpacing: "-0.2px",
    }}
  >
    <svg width={small ? 18 : 22} height={small ? 18 : 22} viewBox="0 0 24 24" fill="currentColor">
      <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.2l2.807 1.626a1 1 0 010 1.734l-2.808 1.626L15.206 12l2.492-2.493zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z" />
    </svg>
    Get it on Google Play
  </a>
);

const Stars: React.FC<{ size?: number; color?: string }> = ({ size = 15, color = AMBER }) => (
  <span style={{ display: "inline-flex", gap: 1, color, lineHeight: 0 }} aria-label="5 out of 5 stars">
    {[0, 1, 2, 3, 4].map((i) => (
      <svg key={i} width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
      </svg>
    ))}
  </span>
);

/* ── Animated SQL console (hero centerpiece) ── */
const QUERY_LINES = [
  { t: "SELECT ", c: INDIGO },
  { t: "title, year, rating", c: "#0E7C66" },
  { t: "\nFROM ", c: INDIGO },
  { t: "movies", c: INK },
  { t: "\nWHERE ", c: INDIGO },
  { t: "rating ", c: INK },
  { t: "> ", c: "#B96A0E" },
  { t: "9.0", c: "#C2410C" },
  { t: "\nORDER BY ", c: INDIGO },
  { t: "year", c: INK },
  { t: ";", c: SUB },
];
const FULL_QUERY = QUERY_LINES.map((s) => s.t).join("");
const RESULTS = [
  ["The Shawshank Redemption", "1994", "9.3"],
  ["The Godfather", "1972", "9.2"],
  ["The Dark Knight", "2008", "9.0"],
  ["12 Angry Men", "1957", "9.0"],
];

const Console: React.FC = () => {
  const [n, setN] = useState(0);
  const [done, setDone] = useState(false);
  const timer = useRef<any>(null);
  useEffect(() => {
    let i = 0;
    timer.current = setInterval(() => {
      i += 1;
      setN(i);
      if (i >= FULL_QUERY.length) {
        clearInterval(timer.current);
        setTimeout(() => setDone(true), 260);
      }
    }, 34);
    return () => clearInterval(timer.current);
  }, []);

  // Build the syntax-highlighted spans, truncated to n visible chars.
  let remaining = n;
  const spans: React.ReactNode[] = [];
  for (let k = 0; k < QUERY_LINES.length && remaining > 0; k++) {
    const seg = QUERY_LINES[k];
    const take = Math.min(seg.t.length, remaining);
    spans.push(
      <span key={k} style={{ color: seg.c, fontWeight: seg.c === INDIGO ? 700 : 500 }}>
        {seg.t.slice(0, take)}
      </span>
    );
    remaining -= take;
  }

  return (
    <div
      style={{
        background: "#FFFFFF",
        borderRadius: 20,
        border: `1px solid ${LINE}`,
        boxShadow: "0 30px 60px -22px rgba(26,34,51,0.28), 0 8px 20px -12px rgba(26,34,51,0.12)",
        overflow: "hidden",
        width: "100%",
        maxWidth: 460,
        fontFamily: MONO,
      }}
    >
      {/* title bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "0.7rem 1rem",
          borderBottom: `1px solid ${LINE}`,
          background: "#FCFBF8",
        }}
      >
        <span style={{ width: 11, height: 11, borderRadius: 99, background: "#F0705A" }} />
        <span style={{ width: 11, height: 11, borderRadius: 99, background: "#F2BE3C" }} />
        <span style={{ width: 11, height: 11, borderRadius: 99, background: "#57C15E" }} />
        <span style={{ marginLeft: 8, color: SUB, fontSize: "0.82rem", fontFamily: SANS, fontWeight: 600 }}>
          movies.db
        </span>
        <span style={{ marginLeft: "auto", color: "#0E7C66", fontSize: "0.78rem", fontFamily: SANS, fontWeight: 700 }}>
          {done ? "4 rows · 1 ms" : "running…"}
        </span>
      </div>
      {/* editor */}
      <pre
        style={{
          margin: 0,
          padding: "1.1rem 1.2rem",
          fontSize: "0.94rem",
          lineHeight: 1.7,
          whiteSpace: "pre-wrap",
          minHeight: 128,
          color: INK,
        }}
      >
        {spans}
        <span className="sqlv-caret" style={{ background: AMBER, color: AMBER }}>
          &nbsp;
        </span>
      </pre>
      {/* result grid */}
      <div
        style={{
          borderTop: `1px solid ${LINE}`,
          opacity: done ? 1 : 0,
          transform: done ? "translateY(0)" : "translateY(6px)",
          transition: "opacity .45s ease, transform .45s ease",
          fontFamily: SANS,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 64px 64px",
            padding: "0.55rem 1.2rem",
            fontSize: "0.72rem",
            fontWeight: 800,
            letterSpacing: "0.4px",
            textTransform: "uppercase",
            color: INDIGO,
            background: "#F6F5FF",
          }}
        >
          <span>title</span>
          <span>year</span>
          <span>rating</span>
        </div>
        {RESULTS.map((r, i) => (
          <div
            key={i}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 64px 64px",
              padding: "0.6rem 1.2rem",
              fontSize: "0.86rem",
              color: INK,
              borderTop: i ? `1px solid ${LINE}` : "none",
            }}
          >
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r[0]}</span>
            <span style={{ color: SUB }}>{r[1]}</span>
            <span style={{ color: AMBER_D, fontWeight: 700 }}>{r[2]}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const Phone: React.FC<{ src: string; alt: string; w?: number }> = ({ src, alt, w = 240 }) => (
  <div
    style={{
      width: w,
      borderRadius: w * 0.13,
      background: "#0B0F1A",
      padding: w * 0.02,
      boxShadow: "0 30px 60px -20px rgba(26,34,51,0.26), 0 10px 24px -14px rgba(26,34,51,0.14)",
    }}
  >
    <div style={{ borderRadius: w * 0.11, overflow: "hidden", background: "#fff", lineHeight: 0 }}>
      <img src={src} alt={alt} loading="lazy" style={{ width: "100%", height: "auto", display: "block" }} />
    </div>
  </div>
);

const FEATURES: { src: string; label: string; sub: string }[] = [
  {
    src: SHOT + "/05.png",
    label: "Run real SQL, instantly",
    sub: "A full editor with a Run button and sample databases. Write SELECT, JOIN, GROUP BY — see rows in milliseconds.",
  },
  {
    src: SHOT + "/03.png",
    label: "Learn SQL, built in",
    sub: "16 interactive lessons in plain English — from SELECT and WHERE to JOINs and aggregations. Practice as you read.",
  },
  {
    src: SHOT + "/04.png",
    label: "Design schemas visually",
    sub: "Create tables, set types, keys and constraints — no DDL to memorise. The app writes the CREATE TABLE for you.",
  },
  {
    src: SHOT + "/02.png",
    label: "Browse tables like a pro",
    sub: "Open any .db or .sqlite file and scroll clean, searchable rows. Tap a cell to inspect or edit in place.",
  },
];

const FAQ: { q: string; a: string }[] = [
  {
    q: "Can I open my own .db or .sqlite file?",
    a: "Yes. Open any SQLite database file from your phone in a couple of taps — browse its tables, run queries against it, and export the results. Your file never leaves your device.",
  },
  {
    q: "Do I need an internet connection?",
    a: "No. SQL Editor & SQLite Viewer runs entirely offline. Everything — the editor, the schema designer, the Learn SQL lessons and the sample databases — works with no account and no connection.",
  },
  {
    q: "Is it really free?",
    a: "Yes. The full database manager is free. An optional one-time Pro upgrade removes ads — there is no subscription.",
  },
  {
    q: "I'm new to SQL. Is this for me?",
    a: "Absolutely. The built-in Try SQL playground ships with Movies and School sample databases, plus 16 interactive lessons, so you can learn and practise real queries without any setup.",
  },
];

const SQLVEditLanding: React.FC = () => {
  const [showBar, setShowBar] = useState(false);
  const [open, setOpen] = useState<number | null>(0);
  useEffect(() => {
    const onScroll = () => setShowBar(window.scrollY > 640);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const softwareLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "SQL Editor & SQLite Viewer",
    operatingSystem: "Android",
    applicationCategory: "DeveloperApplication",
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    aggregateRating: { "@type": "AggregateRating", ratingValue: RATING, ratingCount: RATING_COUNT },
    review: [
      {
        "@type": "Review",
        reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
        author: { "@type": "Person", name: "Google Play user" },
        reviewBody: REVIEW_WOW,
      },
      {
        "@type": "Review",
        reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
        author: { "@type": "Person", name: "Google Play user" },
        reviewBody: REVIEW_LEARN,
      },
    ],
    description:
      "Open, browse and edit SQLite databases on Android. Run SQL queries, design schemas visually, learn SQL with 16 interactive lessons, and import/export CSV — free, offline, no account.",
    downloadUrl: PLAY,
    image: "https://cosmovexapps.com" + LOGO,
  };

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <>
      <Helmet>
        <title>SQL Editor & SQLite Viewer for Android — Open .db Files, Run Queries & Learn SQL</title>
        <meta
          name="description"
          content="Open any SQLite database on your phone. Browse tables, run SQL queries, design schemas visually, and learn SQL with 16 interactive lessons. Free, offline, no account — the SQLite manager that fits in your pocket."
        />
        <link rel="canonical" href="https://cosmovexapps.com/apps/sqlvedit" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="SQL Editor & SQLite Viewer for Android" />
        <meta
          property="og:description"
          content="Open .db files, run SQL queries, design schemas and learn SQL — free, offline, no account. A full SQLite manager in your pocket."
        />
        <meta property="og:image" content={"https://cosmovexapps.com" + LOGO} />
        <meta property="og:url" content="https://cosmovexapps.com/apps/sqlvedit" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="SQL Editor & SQLite Viewer for Android" />
        <meta
          name="twitter:description"
          content="Open .db files, run SQL queries, design schemas and learn SQL — free & offline."
        />
        <meta name="twitter:image" content={"https://cosmovexapps.com" + LOGO} />
        <meta
          name="keywords"
          content="sqlite editor, sqlite viewer, sql editor android, database viewer, db viewer, sqlite manager, open db file android, open sqlite file, run sql queries android, learn sql app, sqlite database manager, sql playground android"
        />
        <script type="application/ld+json">{JSON.stringify(softwareLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
      </Helmet>

      <div style={{ background: IVORY, minHeight: "100vh", fontFamily: SANS, color: INK }}>
        {/* NAV */}
        <nav
          style={{
            maxWidth: 1180,
            margin: "0 auto",
            padding: "1.3rem 1.5rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "0.7rem" }}>
            <img src={LOGO} alt="SQL Editor & SQLite Viewer logo" width={44} height={44} style={{ borderRadius: 12 }} />
            <span style={{ fontWeight: 700, fontSize: "1.12rem" }}>SQL Editor &amp; SQLite Viewer</span>
          </div>
          <PlayButton small />
        </nav>

        {/* HERO */}
        <header
          className="sqlv-hero"
          style={{
            maxWidth: 1180,
            margin: "0 auto",
            padding: "2.4rem 1.5rem 1rem",
            display: "grid",
            gridTemplateColumns: "1.02fr 0.98fr",
            gap: "2.4rem",
            alignItems: "center",
          }}
        >
          <div>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                background: "#FFFFFF",
                border: `1px solid ${LINE}`,
                color: INK,
                fontWeight: 700,
                fontSize: "0.9rem",
                padding: "0.42rem 0.85rem",
                borderRadius: 999,
                marginBottom: "1.3rem",
                boxShadow: "0 2px 8px -4px rgba(26,34,51,0.12)",
              }}
            >
              <Stars size={14} /> {RATING} · 1K+ installs · Free
            </div>
            <h1
              style={{
                fontWeight: 800,
                fontSize: "clamp(2.5rem,6vw,4.3rem)",
                lineHeight: 1.02,
                letterSpacing: "-2.4px",
                margin: 0,
              }}
            >
              The whole of SQLite,<br />
              <span
                style={{
                  background: `linear-gradient(90deg, ${AMBER_D}, ${AMBER})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                in your pocket.
              </span>
            </h1>
            <p style={{ fontSize: "1.3rem", color: "#38424F", lineHeight: 1.45, margin: "1.35rem 0 2rem", maxWidth: 520 }}>
              Open any <code style={{ fontFamily: MONO, fontSize: "0.95em", color: AMBER_D }}>.db</code> file, browse
              tables, run real SQL queries, design schemas — and actually learn SQL along the way. Free, offline, no
              account.
            </p>
            <PlayButton />
            <p style={{ color: "#8A93A2", fontSize: "0.95rem", margin: "0.95rem 0 0" }}>
              Free on Android · Works fully offline · No sign-up
            </p>
          </div>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Console />
          </div>
        </header>

        {/* LOGO STRIP / trust line */}
        <div
          style={{
            maxWidth: 1180,
            margin: "1.5rem auto 0",
            padding: "0 1.5rem",
            display: "flex",
            gap: "0.7rem",
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          {["Open .db & .sqlite", "SQL editor + Run", "Schema designer", "ER diagram", "Charts", "CSV import / export", "Learn SQL"].map(
            (t) => (
              <span
                key={t}
                style={{
                  background: "#FFFFFF",
                  border: `1px solid ${LINE}`,
                  color: INK,
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  padding: "0.5rem 1rem",
                  borderRadius: 999,
                }}
              >
                {t}
              </span>
            )
          )}
        </div>

        {/* FEATURES — alternating screenshot rows */}
        <section style={{ maxWidth: 1120, margin: "0 auto", padding: "3.5rem 1.5rem 1rem" }}>
          <h2
            style={{
              fontWeight: 800,
              fontSize: "clamp(2rem,5vw,3.1rem)",
              letterSpacing: "-1.5px",
              textAlign: "center",
              margin: 0,
            }}
          >
            Not a viewer. A database studio.
          </h2>
          <p
            style={{
              fontSize: "1.2rem",
              color: SUB,
              textAlign: "center",
              margin: "0.8rem auto 3rem",
              maxWidth: 620,
              lineHeight: 1.5,
            }}
          >
            Everything you'd reach for on a desktop SQLite tool — the editor, the schema designer, the ER diagram —
            rebuilt for a phone that stays in your hand.
          </p>

          {FEATURES.map((f, i) => (
            <div
              key={f.label}
              className="sqlv-row"
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "2.5rem",
                alignItems: "center",
                margin: "0 0 3.2rem",
              }}
            >
              <div
                style={{
                  order: i % 2 === 0 ? 0 : 2,
                  display: "flex",
                  justifyContent: "center",
                  background: i % 2 === 0 ? "#F6F5FF" : "#FBF3E7",
                  borderRadius: 28,
                  padding: "2.4rem 1rem 0",
                  overflow: "hidden",
                  maxHeight: 460,
                }}
              >
                <Phone src={f.src} alt={f.label} w={224} />
              </div>
              <div style={{ order: 1 }}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 12,
                    background: i % 2 === 0 ? "#EAE9FF" : "#FCEBD5",
                    color: i % 2 === 0 ? INDIGO : AMBER_D,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 800,
                    fontFamily: MONO,
                    marginBottom: "1rem",
                  }}
                >
                  {i + 1}
                </div>
                <h3 style={{ fontWeight: 800, fontSize: "clamp(1.6rem,3.4vw,2.2rem)", letterSpacing: "-1px", margin: 0 }}>
                  {f.label}
                </h3>
                <p style={{ fontSize: "1.14rem", color: "#41505F", lineHeight: 1.55, margin: "0.8rem 0 0", maxWidth: 440 }}>
                  {f.sub}
                </p>
              </div>
            </div>
          ))}
        </section>

        {/* REVIEWS */}
        <section
          style={{
            background: "#FFFFFF",
            borderTop: `1px solid ${LINE}`,
            borderBottom: `1px solid ${LINE}`,
            padding: "4rem 1.5rem",
          }}
        >
          <div style={{ maxWidth: 1120, margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: "2.6rem" }}>
              <Stars size={22} />
              <h2 style={{ fontWeight: 800, fontSize: "clamp(2rem,5vw,3rem)", letterSpacing: "-1.5px", margin: "0.7rem 0 0.4rem" }}>
                Rated {RATING} on Google Play
              </h2>
              <p style={{ fontSize: "1.15rem", color: SUB, margin: 0 }}>
                Loved by students learning SQL and developers shipping apps.
              </p>
            </div>

            {/* Standout review */}
            <figure
              style={{
                maxWidth: 760,
                margin: "0 auto 2.4rem",
                background: "linear-gradient(180deg, #FFFDF8 0%, #FBF3E7 100%)",
                border: `1px solid ${LINE}`,
                borderRadius: 26,
                padding: "2.4rem 2.2rem",
                boxShadow: "0 24px 50px -28px rgba(184,106,14,0.4)",
                textAlign: "center",
              }}
            >
              <Stars size={19} />
              <blockquote
                style={{
                  fontSize: "clamp(1.25rem,2.6vw,1.6rem)",
                  lineHeight: 1.5,
                  fontWeight: 600,
                  letterSpacing: "-0.4px",
                  color: INK,
                  margin: "1rem 0 1.4rem",
                }}
              >
                “Is it seriously so hard to build a working SQLite client for Android?! This is the first one I've found
                that just <span style={{ color: AMBER_D }}>WORKS</span> flawlessly. Actually opens my db files, actually
                lets me interact with the fields, and it's PRETTY! with a nice UI. Thank you!{" "}
                <span style={{ color: AMBER_D }}>6 stars.</span>”
              </blockquote>
              <figcaption style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.7rem" }}>
                <span
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 999,
                    background: INDIGO,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 800,
                    fontSize: "1.1rem",
                  }}
                >
                  ★
                </span>
                <span style={{ textAlign: "left" }}>
                  <strong style={{ display: "block", fontSize: "1rem" }}>Verified review</strong>
                  <span style={{ color: SUB, fontSize: "0.9rem" }}>Google Play</span>
                </span>
              </figcaption>
            </figure>

            {/* Secondary review */}
            <figure
              style={{
                maxWidth: 640,
                margin: "0 auto",
                background: IVORY,
                border: `1px solid ${LINE}`,
                borderRadius: 20,
                padding: "1.8rem 1.9rem",
              }}
            >
              <Stars size={15} />
              <blockquote
                style={{ fontSize: "1.08rem", color: "#2E3947", lineHeight: 1.6, margin: "0.7rem 0 1.2rem", fontWeight: 500 }}
              >
                {REVIEW_LEARN}
              </blockquote>
              <figcaption style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                <span
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 999,
                    background: "#EAE9FF",
                    color: INDIGO,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 800,
                    fontSize: "0.85rem",
                  }}
                >
                  ★
                </span>
                <span style={{ fontSize: "0.88rem" }}>
                  <strong>Verified review</strong>
                  <span style={{ color: SUB }}> · Google Play · learning SQL on a phone</span>
                </span>
              </figcaption>
            </figure>
          </div>
        </section>

        {/* FAQ */}
        <section style={{ maxWidth: 780, margin: "0 auto", padding: "4rem 1.5rem 1rem" }}>
          <h2
            style={{
              fontWeight: 800,
              fontSize: "clamp(2rem,5vw,2.8rem)",
              letterSpacing: "-1.5px",
              textAlign: "center",
              margin: "0 0 2.2rem",
            }}
          >
            Questions, answered
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.9rem" }}>
            {FAQ.map((f, i) => (
              <div
                key={f.q}
                style={{ background: "#FFFFFF", border: `1px solid ${LINE}`, borderRadius: 16, overflow: "hidden" }}
              >
                <button
                  onClick={() => setOpen(open === i ? null : i)}
                  aria-expanded={open === i}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "1rem",
                    padding: "1.15rem 1.4rem",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    textAlign: "left",
                    font: "inherit",
                    fontWeight: 700,
                    fontSize: "1.08rem",
                    color: INK,
                  }}
                >
                  {f.q}
                  <span style={{ color: AMBER, fontSize: "1.4rem", lineHeight: 1, transform: open === i ? "rotate(45deg)" : "none", transition: "transform .2s" }}>
                    +
                  </span>
                </button>
                {open === i && (
                  <p style={{ margin: 0, padding: "0 1.4rem 1.3rem", color: "#41505F", fontSize: "1.02rem", lineHeight: 1.6 }}>
                    {f.a}
                  </p>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* FINAL CTA */}
        <section style={{ padding: "3.5rem 1.5rem 4.5rem" }}>
          <div
            style={{
              maxWidth: 900,
              margin: "0 auto",
              background: `linear-gradient(135deg, #1A2233 0%, #2A2350 100%)`,
              borderRadius: 30,
              padding: "3.4rem 2rem",
              textAlign: "center",
              color: "#fff",
              boxShadow: "0 30px 60px -28px rgba(26,34,51,0.5)",
            }}
          >
            <h2 style={{ fontWeight: 800, fontSize: "clamp(2rem,5vw,3rem)", letterSpacing: "-1.5px", margin: 0 }}>
              Put SQL in your pocket.
            </h2>
            <p style={{ fontSize: "1.2rem", color: "#C7CBDC", lineHeight: 1.5, margin: "1rem auto 2rem", maxWidth: 540 }}>
              Free to download. No account, no subscription, no connection needed. Open a database and start querying in
              seconds.
            </p>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <PlayButton />
            </div>
          </div>
        </section>

        {/* FOOTER */}
        <footer
          style={{
            maxWidth: 1180,
            margin: "0 auto",
            padding: "1rem 1.5rem 6rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "1rem",
            color: "#8A93A2",
            fontSize: "0.95rem",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
            <img src={LOGO} alt="" width={32} height={32} style={{ borderRadius: 9 }} />
            <span>© Cosmovex · SQL Editor &amp; SQLite Viewer</span>
          </div>
          <div style={{ display: "flex", gap: "1.4rem" }}>
            <a href="/sqlvedit/privacy-policy.html" style={{ color: SUB, textDecoration: "none" }}>
              Privacy
            </a>
            <a href="/sqlvedit/support.html" style={{ color: SUB, textDecoration: "none" }}>
              Support
            </a>
            <a href={PLAY} onClick={playClick} style={{ color: AMBER_D, fontWeight: 700, textDecoration: "none" }}>
              Get the app
            </a>
          </div>
        </footer>
      </div>

      {/* STICKY INSTALL BAR */}
      {showBar && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
            padding: "0.65rem 1rem calc(0.65rem + env(safe-area-inset-bottom))",
            background: "rgba(251,250,246,0.96)",
            backdropFilter: "blur(14px)",
            borderTop: `1px solid ${LINE}`,
          }}
        >
          <img src={LOGO} alt="" width={40} height={40} style={{ borderRadius: 10 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: "0.95rem" }}>SQL Editor &amp; SQLite Viewer</div>
            <div style={{ color: SUB, fontSize: "0.78rem" }}>★ {RATING} · Free on Google Play</div>
          </div>
          <PlayButton small />
        </div>
      )}

      <style>{`
        .sqlv-caret{ display:inline-block; width:0.5rem; animation: sqlvBlink 1s steps(1) infinite; border-radius:2px; }
        @keyframes sqlvBlink{ 50%{ opacity:0; } }
        @media (max-width: 860px){
          .sqlv-hero{ grid-template-columns: 1fr !important; text-align:center; }
          .sqlv-hero > div:first-child p{ margin-left:auto; margin-right:auto; }
          .sqlv-hero > div:last-child{ margin-top:0.5rem; }
          .sqlv-row{ grid-template-columns: 1fr !important; gap:1.4rem !important; text-align:center; }
          .sqlv-row > div:first-child{ order:0 !important; }
          .sqlv-row > div:last-child{ order:1 !important; }
          .sqlv-row > div:last-child > div:first-child{ margin-left:auto; margin-right:auto; }
          .sqlv-row p{ margin-left:auto; margin-right:auto; }
        }
      `}</style>
    </>
  );
};

export default SQLVEditLanding;
